import { useState } from "react";
import InputGroupDateRange from "./InputGroupDateRange";

## Basic Usage

A date range picker with two native date inputs. The start date's `max` is constrained to the end date and vice versa, so the browser prevents invalid ranges.

export function BasicExample() {
  const [start, setStart] = useState("2026-01-01");
  const [end, setEnd] = useState("2026-02-25");
  return (
    <div>
      <InputGroupDateRange startDate={start} endDate={end} onStartChange={setStart} onEndChange={setEnd} />
      <p style={{ marginTop: "0.5rem", fontSize: "13px", color: "var(--secondary-text-color)" }}>
        Selected: {start} — {end}
      </p>
    </div>
  );
}

<BasicExample />

---

## With Presets

Add quick-select presets for common ranges. The dropdown highlights the active preset when the dates match.

export function PresetsExample() {
  const [start, setStart] = useState("2026-01-27");
  const [end, setEnd] = useState("2026-02-25");
  return (
    <InputGroupDateRange
      startDate={start}
      endDate={end}
      onStartChange={setStart}
      onEndChange={setEnd}
      presets={["last7", "last30", "last90", "thisMonth", "thisYear"]}
    />
  );
}

<PresetsExample />

---

## With Label

Wrap the range in an `InputGroup` label by passing `labelText`.

export function LabelExample() {
  const [start, setStart] = useState("2026-02-01");
  const [end, setEnd] = useState("2026-02-25");
  return (
    <InputGroupDateRange
      labelText="Date Range"
      startDate={start}
      endDate={end}
      onStartChange={setStart}
      onEndChange={setEnd}
      presets={["last7", "last30"]}
    />
  );
}

<LabelExample />

---

## Value Format: datetime

Set `valueFormat="datetime"` to emit `YYYY-MM-DDT00:00:00` for start and `YYYY-MM-DDT23:59:59` for end. No timezone suffix — the consumer decides how to interpret or append one.

export function DatetimeFormatExample() {
  const [start, setStart] = useState("2026-01-01T00:00:00");
  const [end, setEnd] = useState("2026-02-25T23:59:59");
  return (
    <div>
      <InputGroupDateRange
        startDate={start}
        endDate={end}
        onStartChange={setStart}
        onEndChange={setEnd}
        valueFormat="datetime"
      />
      <div style={{ marginTop: "0.5rem", fontSize: "12px", fontFamily: "monospace", color: "var(--secondary-text-color)" }}>
        <div>start: {start}</div>
        <div>end: {end}</div>
      </div>
    </div>
  );
}

<DatetimeFormatExample />

---

## Custom Labels

Override the default "From" / "To" labels.

export function CustomLabelsExample() {
  const [start, setStart] = useState("2026-02-01");
  const [end, setEnd] = useState("2026-02-28");
  return (
    <InputGroupDateRange
      startDate={start}
      endDate={end}
      onStartChange={setStart}
      onEndChange={setEnd}
      startLabel="Start"
      endLabel="End"
    />
  );
}

<CustomLabelsExample />

---

## Sizes

Matches the existing Input component sizes.

export function SizesExample() {
  const [start, setStart] = useState("2026-02-01");
  const [end, setEnd] = useState("2026-02-25");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div>
        <p style={{ marginBottom: "0.5rem" }}><strong>Medium:</strong></p>
        <InputGroupDateRange startDate={start} endDate={end} onStartChange={setStart} onEndChange={setEnd} inputSize="medium" />
      </div>
      <div>
        <p style={{ marginBottom: "0.5rem" }}><strong>Medium Large (default):</strong></p>
        <InputGroupDateRange startDate={start} endDate={end} onStartChange={setStart} onEndChange={setEnd} inputSize="mediumLarge" />
      </div>
      <div>
        <p style={{ marginBottom: "0.5rem" }}><strong>Large:</strong></p>
        <InputGroupDateRange startDate={start} endDate={end} onStartChange={setStart} onEndChange={setEnd} inputSize="large" />
      </div>
    </div>
  );
}

<SizesExample />

---

## Disabled

export function DisabledExample() {
  return (
    <InputGroupDateRange startDate="2026-02-01" endDate="2026-02-25" onStartChange={() => {}} onEndChange={() => {}} disabled />
  );
}

<DisabledExample />
